import { effect, inject } from '@angular/core';
import { signalStore, withHooks, withMethods, withState, patchState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, catchError, pipe, switchMap, tap } from 'rxjs';

import { AuthStore } from '@core/auth';
import { onProfileChange } from '@core/profiles';
import { onLanguageChange } from '@core/localization';

import { MenuApiService } from './menu-api.service';
import { IMenuEntry } from './menu.models';

// Holds the operator-driven side-nav. Auto-loads when AuthStore.customer()
// resolves (anonymous customers get the menu too — V1 doesn't gate the
// endpoint behind a real account).

interface MenuState {
    items: IMenuEntry[];
    loaded: boolean;
}

const initialState: MenuState = {
    items: [],
    loaded: false,
};

export const MenuStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),

    withMethods((store, api = inject(MenuApiService)) => ({
        load: rxMethod<void>(pipe(
            switchMap(() => api.getMenu().pipe(
                tap(items => patchState(store, { items, loaded: true })),
                catchError(() => EMPTY),
            )),
        )),

        clear(): void {
            patchState(store, initialState);
        },
    })),

    withHooks({
        onInit(store, auth = inject(AuthStore)) {
            // Refetch whenever a customer becomes available, drop on sign-out.
            effect(() => {
                const customer = auth.customer();
                if (customer) {
                    if (!store.loaded()) store.load();
                } else {
                    store.clear();
                }
            });

            // The menu is profile-scoped too (AVIION-36668). Refetch rather than clear:
            // it is always on screen, and the effect above only reloads on a customer
            // change, so blanking it here would leave the side-nav empty.
            // Only with a live session: logout clears the profile too, and a load fired
            // then goes out with dead cookies — its 401 triggers a refresh that also 401s,
            // and that teardown wipes the freshly created anonymous session.
            onProfileChange(() => {
                if (auth.customer()) store.load();
            });

            // Menu labels are backend-provided (not translation keys) and localized by the
            // Language-Reference-Id header, so a runtime language switch left the side-nav in
            // the old language until a full reload. Refetch — same reasoning as the profile
            // hook: the menu is always on screen, so blanking it would leave it empty.
            onLanguageChange(() => {
                if (auth.customer()) store.load();
            });
        },
    }),
);
